<template>
    <div>
        <div v-if="loading" class="text-center" style="padding: 40px 0;">
            <font-awesome-icon :icon="['fas', 'spinner']" spin size="2x" />
        </div>
        <div v-else>
            <div class="row mb-2">
                <div class="col-12">
                    <span class="text-muted small me-2">When:</span>
                    <span v-if="useNoon">Noon</span><a v-else href="#" @click.prevent="setNoon">Noon</a>
                    <span class="text-muted small">&nbsp;/&nbsp;</span>
                    <a v-if="useNoon" href="#" @click.prevent="setNow">Now</a><span v-else>Now</span>
                </div>
            </div>
            <div class="row">
                <div class="col-md-3" v-for="planet in planetsSummary" :key="planet.id">
                    <div class="card w-100 margin-botton-20" style="width: 18rem;">
                        <div class="card-body event-card-bg">
                            <h3 class="h5 card-title">{{ planet.planet_name }}</h3>
                            <p class="symbol-display">{{ planet.planet_symbol }}</p>
                            <p class="card-text">
                                <span v-if="planet.altitude_azimuth.is_visible" class="badge bg-success text-white">Visible</span>
                                <span v-if="!planet.altitude_azimuth.is_visible" class="badge bg-secondary text-white">Not visible</span>
                            </p>
                            <p class="card-text">
                                <span style="font-size: 130%">{{ planet.altitude_azimuth.formatted_azimuth }}</span>
                                <br>{{ planet.altitude_azimuth.formatted_altitude }} <small>altitude</small>
                                <template v-if="planet.rising_setting && planet.rising_setting.events && planet.rising_setting.events.length >= 2">
                                <br><small>{{ planet.rising_setting.events[1].name }} at </small>{{ planet.rising_setting.events[1].time }}
                                <br><small>{{ planet.rising_setting.events[0].name }} at </small>{{ planet.rising_setting.events[0].time }}
                                </template>
                                <br>{{ planet.distance_to_earth.formatted_distance_km }} <small>Km to Earth</small>
                                <br>{{ planet.distance_to_sun.formatted_distance_km }} <small>Km to Sun</small>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row mt-3" v-if="planetsSummary.length">
                <div class="col-12">
                    <div class="card w-100 margin-botton-20">
                        <div class="card-body event-card-bg">
                            <PlanetDistanceChart :planets="planetsSummary" title="Planet Positions" :subtitle="chartSubtitle" :subtitle2="chartSubtitle2" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="row mt-3" v-if="planetMonthlyRows.length">
                <div class="col-12">
                    <div class="card w-100 margin-botton-20">
                        <div class="card-body event-card-bg">
                            <h3 class="h4 card-title mb-3">{{ currentMonthLabel }}</h3>
                            <div class="mb-3">
                                <div class="form-check form-check-inline" v-for="planet in planetsSummary" :key="planet.planet_name">
                                    <input class="form-check-input" type="radio" :id="'planet-' + planet.planet_name" :value="planet.planet_name" v-model="selectedPlanet">
                                    <label class="form-check-label" :for="'planet-' + planet.planet_name">{{ planet.planet_name }}</label>
                                </div>
                            </div>
                            <div v-if="planetMonthlyLoading" class="text-center" style="padding: 20px 0;">
                                <font-awesome-icon :icon="['fas', 'spinner']" spin size="2x" />
                            </div>
                            <div v-else class="table-responsive">
                            <table class="table table-sm mb-0">
                                <thead>
                                    <tr>
                                        <th>Day</th>
                                        <th>Rising</th>
                                        <th>Setting</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in planetMonthlyRows" :key="row.day"
                                        :class="{ 'table-success': date && parseInt(row.day) === date.getDate() }"
                                        :style="date && parseInt(row.day) === date.getDate() ? { '--bs-table-bg': 'rgba(25,135,84,0.15)', '--bs-table-border-color': 'transparent' } : {}">
                                        <td>{{ row.day }}</td>
                                        <td>{{ row.rising }}</td>
                                        <td>{{ row.setting }}</td>
                                    </tr>
                                </tbody>
                            </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import astroTabMixin from '@/mixins/astroTab'
import astra from '@/api/astra'
import PlanetDistanceChart from './PlanetDistanceChart'

export default {
    name: 'TabPlanets',
    mixins: [astroTabMixin],
    components: { PlanetDistanceChart },
    props: {
        active: { type: Boolean, default: false },
    },
    data() {
        return {
            loading: true,
            planetsSummary: [],
            selectedPlanet: null,
            planetMonthly: [],
            planetMonthlyLoading: false,
        }
    },
    computed: {
        chartSubtitle() {
            if (!this.city || !this.city.name) return ''
            const parts = [this.city.name]
            if (this.city.region_name) parts.push(this.city.region_name)
            return 'near ' + parts.join(', ')
        },
        chartSubtitle2() {
            if (!this.date) return ''
            return 'for ' + this.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        },
        planetMonthlyRows() {
            const byDate = {}
            for (const event of this.planetMonthly) {
                if (!byDate[event.short_date]) byDate[event.short_date] = {}
                if (event.name === 'Rising') byDate[event.short_date].rising = event.time
                else if (event.name === 'Setting') byDate[event.short_date].setting = event.time
            }
            return Object.entries(byDate)
                .map(([date, times]) => ({
                    day: date.split('-')[2],
                    rising: times.rising || '—',
                    setting: times.setting || '—',
                }))
                .sort((a, b) => parseInt(a.day) - parseInt(b.day))
        },
    },
    watch: {
        active(val) { if (val) this.load() },
        city()      { if (this.active) this.load() },
        date()      { if (this.active) this.load() },
        selectedPlanet() { this.loadMonthly() },
    },
    mounted() {
        if (this.active) this.load()
    },
    methods: {
        loadMonthly() {
            if (!this.selectedPlanet || !this.planetsSummary.length) return
            const planet = this.planetsSummary.find(p => p.planet_name === this.selectedPlanet) || this.planetsSummary[0]
            if (!planet) return
            this.planetMonthlyLoading = true
            astra.fetchBodyRiseSet({ ...this.loc, ...this.monthlyRange, body: planet.rising_setting.body })
                .then(r => { this.planetMonthly = r.data.events })
                .finally(() => { this.planetMonthlyLoading = false })
        },
        async load() {
            if (!this.latitude || !this.longitude || !this.timezone) return
            if (this.apiLocked) return
            this.loading = true
            await astra.fetchPlanetsSummary({ ...this.loc, date1: this.dailyTimeRange.date1 }).then(r => {
                this.planetsSummary = r.data.results
                if (!this.selectedPlanet && r.data.results.length) {
                    this.selectedPlanet = r.data.results[0].planet_name
                    // watcher fires → loadMonthly() called
                } else {
                    this.loadMonthly()
                }
            })
            this.loading = false
        },
    },
}
</script>

<style scoped>
.margin-botton-20 { margin-bottom: 20px; }
.symbol-display {
    font-size: 2.5rem;
    margin: 0;
    padding: 0;
    line-height: 1.2;
}
.table-responsive td,
.table-responsive th { white-space: nowrap; }
</style>
